import { useEffect, useMemo, useRef } from 'react'
import { useElapsedMs } from '@/hooks/useElapsed'
import type { IntervalPosition, IntervalStep } from '@/domain/timeline'
import { buildIntervalSteps, durationBefore, locateInterval, phaseDuration, workoutDuration } from '@/domain/timeline'
import type { Phase } from '@/domain/workout'
import { audioCues } from '@/services/audioCues'
import { selectPhase, useSessionStore } from '@/store/sessionStore'
import { clamp } from '@/lib/format'

/** Segundos de aviso sonoro antes de que se acabe un tramo. */
const WARNING_SECONDS = 3

export interface PhaseRuntime {
  phase: Phase | undefined
  /** Segundos transcurridos del bloque, con decimales. */
  elapsed: number
  /** Segundos que faltan para cerrar el bloque o el tramo en curso. */
  remaining: number
  /** Avance del bloque, de 0 a 1. */
  progress: number
  /** Avance de la sesión completa, de 0 a 1. */
  overall: number
  /** Solo en intervalos: en qué tramo estamos. */
  position: IntervalPosition | null
  steps: readonly IntervalStep[]
  running: boolean
}

/**
 * Traduce el reloj en el estado visible del bloque y dispara lo que va con él:
 * los pitidos y el cierre automático de la fase. Es el único sitio donde
 * "se acabó el tiempo" tiene consecuencias.
 */
export function usePhaseRunner(): PhaseRuntime {
  const phase = useSessionStore(selectPhase)
  const week = useSessionStore((state) => state.week)
  const phaseIndex = useSessionStore((state) => state.phaseIndex)
  const stage = useSessionStore((state) => state.stage)
  const clock = useSessionStore((state) => state.clock)
  const completePhase = useSessionStore((state) => state.completePhase)

  const elapsedMs = useElapsedMs(clock)
  const elapsed = elapsedMs / 1000
  const running = clock.status === 'running' && stage === 'work'

  const steps = useMemo(
    () => (phase?.kind === 'intervals' ? buildIntervalSteps(phase) : []),
    [phase],
  )

  const position = useMemo(
    () => (phase?.kind === 'intervals' ? locateInterval(steps, elapsed) : null),
    [phase, steps, elapsed],
  )

  const runtime = useMemo<Omit<PhaseRuntime, 'phase' | 'steps' | 'position' | 'running'>>(() => {
    if (!phase || !week) return { elapsed: 0, remaining: 0, progress: 0, overall: 0 }

    const total = workoutDuration(week.workout)
    const before = durationBefore(week.workout, phaseIndex)

    const progress = (() => {
      switch (phase.kind) {
        case 'amrap':
        case 'countdown':
          return clamp(elapsed / phase.durationSeconds, 0, 1)
        case 'intervals': {
          const done = steps.slice(0, position?.stepIndex ?? 0).reduce((sum, step) => sum + step.seconds, 0)
          const inStep = (position?.step.seconds ?? 0) * (position?.stepProgress ?? 0)
          return clamp((done + inStep) / Math.max(1, phaseDuration(phase)), 0, 1)
        }
        case 'afap':
          // En AFAP el avance lo marcan los bloques marcados, no el reloj.
          return 0
      }
    })()

    const remaining = (() => {
      switch (phase.kind) {
        case 'amrap':
        case 'countdown':
          return Math.max(0, Math.ceil(phase.durationSeconds - elapsed))
        case 'intervals':
          return position?.remainingInStep ?? 0
        case 'afap':
          return Math.max(0, Math.ceil(phase.capSeconds - elapsed))
      }
    })()

    return {
      elapsed,
      remaining,
      progress,
      overall: clamp((before + phaseDuration(phase) * progress) / Math.max(1, total), 0, 1),
    }
  }, [phase, week, phaseIndex, elapsed, steps, position])

  /* ── Avisos sonoros ─────────────────────────────────────────────────── */
  const lastCountRef = useRef<number | null>(null)
  const lastStepRef = useRef<number | null>(null)

  // Al cambiar de bloque se olvida lo sonado en el anterior: si no, el primer
  // tramo del bloque nuevo se leería como un cambio de ejercicio y pitaría.
  useEffect(() => {
    lastCountRef.current = null
    lastStepRef.current = null
  }, [phaseIndex])

  useEffect(() => {
    if (!running || !phase) {
      lastCountRef.current = null
      return
    }
    // El AFAP cuenta hacia arriba: no hay cuenta atrás que avisar.
    if (phase.kind === 'afap') return

    const seconds = runtime.remaining
    if (seconds > 0 && seconds <= WARNING_SECONDS && lastCountRef.current !== seconds) {
      lastCountRef.current = seconds
      audioCues.play('count')
    }
    if (seconds > WARNING_SECONDS) lastCountRef.current = null
  }, [running, phase, runtime.remaining])

  useEffect(() => {
    if (!running || phase?.kind !== 'intervals' || !position) return

    if (lastStepRef.current !== null && lastStepRef.current !== position.stepIndex) {
      audioCues.play(position.step.kind === 'rest' ? 'rest' : 'stepChange')
    }
    lastStepRef.current = position.stepIndex
  }, [running, phase, position])

  /* ── Cierre automático del bloque ───────────────────────────────────── */
  useEffect(() => {
    if (!running || !phase) return

    const finished =
      phase.kind === 'intervals'
        ? (position?.finished ?? false)
        : phase.kind === 'afap'
          ? elapsed >= phase.capSeconds
          : elapsed >= phase.durationSeconds

    if (!finished) return

    audioCues.play('finish')
    completePhase(phase.kind === 'afap' ? phase.capSeconds : undefined)
  }, [running, phase, position, elapsed, completePhase])

  return { phase, ...runtime, position, steps, running }
}
